import { useEffect, useMemo } from "react";
import { Group, Loader, Select, Text, type SelectProps } from "@mantine/core";
import { useUserContracts } from "../../hooks/useUserContracts";
import type { ContractListItem } from "../../types/UserRole";

export interface SettingsContractOption {
  id: number;
  name: string;
}

type UserContractSelectProps = Omit<
  SelectProps,
  "data" | "value" | "onChange" | "disabled"
> & {
  addressSystemId?: number;
  userId?: number;
  contracts?: SettingsContractOption[];
  value: number | null;
  onChange: (contractId: number | null) => void;
  onContractsLoaded?: (contracts: ContractListItem[]) => void;
  disabled?: boolean;
  showLoadingHint?: boolean;
  showEmptyHint?: boolean;
  emptyHint?: string;
};

export default function UserContractSelect({
  addressSystemId,
  userId,
  contracts,
  value,
  onChange,
  onContractsLoaded,
  disabled,
  showLoadingHint = true,
  showEmptyHint = true,
  emptyHint = "No contracts available for this address system.",
  label = "Contract",
  placeholder = "Select a contract",
  searchable = true,
  clearable = true,
  ...selectProps
}: Readonly<UserContractSelectProps>) {
  const useRemoteContracts = contracts == null;
  const { contracts: fetchedContracts, options: fetchedOptions, loading, error } =
    useUserContracts(addressSystemId, userId, useRemoteContracts);

  const options = useMemo(
    () =>
      contracts?.map((contract) => ({
        value: String(contract.id),
        label: contract.name,
      })) ?? fetchedOptions,
    [contracts, fetchedOptions],
  );

  useEffect(() => {
    if (!onContractsLoaded) return;

    if (contracts) {
      onContractsLoaded(
        contracts.map((contract) => ({
          id: contract.id,
          name: contract.name,
        })) as ContractListItem[],
      );
      return;
    }

    onContractsLoaded(fetchedContracts);
  }, [contracts, fetchedContracts, onContractsLoaded]);

  const selectDisabled = disabled || (useRemoteContracts && loading) || options.length === 0;

  return (
    <>
      <Select
        {...selectProps}
        label={label}
        placeholder={placeholder}
        data={options}
        value={value != null ? String(value) : null}
        onChange={(nextValue) =>
          onChange(nextValue ? Number(nextValue) : null)
        }
        searchable={searchable}
        clearable={clearable}
        disabled={selectDisabled}
        rightSection={
          useRemoteContracts && loading ? (
            <Loader size="xs" />
          ) : (
            selectProps.rightSection
          )
        }
      />
      {useRemoteContracts && showLoadingHint && loading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Loading contracts...
          </Text>
        </Group>
      )}
      {useRemoteContracts && error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}
      {showEmptyHint && !loading && !error && options.length === 0 && (
        <Text size="sm" c="dimmed">
          {emptyHint}
        </Text>
      )}
    </>
  );
}
